import React, { Suspense, useContext, useEffect, useState } from 'react';
import { Redirect, Route, RouteComponentProps, Switch, withRouter } from 'react-router-dom';
import { toast } from 'react-toastify';

import onboardingApiUtil from 'src/api/businessOnboarding';
import userApiUtil from 'src/api/loggedInUser';
import CustomToast from 'src/components/CustomToast';
import Loader from 'src/components/Loader';
import BusinessOnboardingFlow, {
  BUSINESS_ONBOARDING_STEP,
  BUSINESS_ONBOARDING_STEPS_MAP,
  OnboardingStepProcess,
} from 'src/containers/BusinessOnboarding';
import { AppContext } from 'src/containers/Core/AppContext';
import { AppState, setBusinessOnboardingDetails, setUser } from 'src/containers/Core/AppReducer';
import Layout from 'src/containers/Core/Layout';
import { ONBOARDING_DETAILS_ERROR, ONBOARDING_START_ERROR, SOMETHING_WENT_WRONG } from 'src/containers/Core/Messages';
import {
  BUSINESS_ONBOARDING,
  FORGOT_PASSWORD,
  INVITATION,
  RESEND_INVITE,
  SET_PASSWORD,
  SIGN_IN,
  SIGN_UP,
  VERIFY_EMAIL,
} from 'src/containers/Core/Paths';
import PreSignInRoutes from 'src/containers/Core/PreSignInRoutes';
import Invite from 'src/containers/UserManagement/Invite';
import { isAuthenticated } from 'src/utils/common';

const Routes: React.FC<RouteComponentProps> = ({ history }) => {
  const [loading, setLoading] = useState(true);
  const { store, dispatch } = useContext(AppContext);
  const { user } = store as AppState;

  const checkBusinessOnboarding = async () => {
    try {
      const response: BusinessOnboardingDetails[] = await onboardingApiUtil.getBusinessOnboarding();
      if (response && response.length) {
        dispatch(setBusinessOnboardingDetails(response[0]));
      } else {
        try {
          const res = await onboardingApiUtil.createBusinessOnboarding({
            state: OnboardingStepProcess.IN,
            step: BUSINESS_ONBOARDING_STEPS_MAP[BUSINESS_ONBOARDING_STEP.ONBOARD_BUSINESS_OWNED],
          });
          dispatch(setBusinessOnboardingDetails(res));
        } catch {
          toast(<CustomToast title={SOMETHING_WENT_WRONG} description={ONBOARDING_START_ERROR} />);
        }
      }
    } catch {
      toast(<CustomToast title={SOMETHING_WENT_WRONG} description={ONBOARDING_DETAILS_ERROR} />);
    }
  };

  const onLoad = async () => {
    try {
      setLoading(true);
      if (isAuthenticated()) {
        if (!user) {
          const userResponse = await userApiUtil.getLoggedInUserDetails();
          dispatch(setUser(userResponse));
        }
      }
    } catch (e) {
      throw e;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    onLoad();
  }, []);

  const handleUserChange = async () => {
    try {
      if (!user.isVerified) {
        return history.replace(VERIFY_EMAIL, { email: user.email });
      }
      await checkBusinessOnboarding();
      history.replace(BUSINESS_ONBOARDING);
    } catch (e) {
      throw e;
    }
  };

  useEffect(() => {
    if (isAuthenticated() && user && !user.isVerified) {
      return history.push(VERIFY_EMAIL, { email: user.email });
    }
  }, [location.pathname]);

  useEffect(() => {
    if (user) {
      handleUserChange();
    }
  }, [user]);

  if (loading) {
    return <Loader height="100vh" />;
  }

  return (
    <Suspense fallback={<Loader />}>
      <Switch>
        <Layout
          path={[SIGN_IN, SIGN_UP, FORGOT_PASSWORD, SET_PASSWORD, VERIFY_EMAIL, RESEND_INVITE]}
          component={PreSignInRoutes}
          leftMenu={false}
          header={false}
          footer
          auth={false}
        />

        <Layout path={BUSINESS_ONBOARDING} component={BusinessOnboardingFlow} />

        <Layout path={INVITATION} component={Invite} leftMenu={false} header={false} auth={isAuthenticated()} />

        <Route render={() => <Redirect to={BUSINESS_ONBOARDING} />} />
      </Switch>
    </Suspense>
  );
};

export default withRouter(Routes);
